/**
 * Shared retry/backoff schedules for player shells. Pure so every surface
 * (telemetry flush, realtime reconnect) retries identically and the math is
 * unit-testable.
 */

export const TELEMETRY_FLUSH_MAX_RETRIES = 5;
export const TELEMETRY_FLUSH_BASE_DELAY_MS = 2_000;

/** Exponential backoff without jitter: base * 2^attempt (attempt is 0-based). */
export function retryDelayMs(
  attempt: number,
  baseMs: number = TELEMETRY_FLUSH_BASE_DELAY_MS,
): number {
  return baseMs * 2 ** Math.max(0, Math.trunc(attempt));
}

export const RECONNECT_MIN_DELAY_MS = 1_000;
export const RECONNECT_MAX_DELAY_MS = 30_000;

/**
 * Reconnect backoff clamped to [min, max]: 1s, 2s, 4s ... 30s. `random` is
 * injectable for deterministic tests; jitter spreads herds after an outage.
 */
export function reconnectDelayMs(
  attempt: number,
  random: () => number = Math.random,
): number {
  const exp = Math.min(
    RECONNECT_MAX_DELAY_MS,
    RECONNECT_MIN_DELAY_MS * 2 ** Math.max(0, Math.trunc(attempt)),
  );
  const jitter = 0.85 + random() * 0.3;
  return Math.round(Math.min(RECONNECT_MAX_DELAY_MS, exp * jitter));
}

/** Missed-interval multiplier before a realtime socket is declared dead. */
export const LIVENESS_MISSED_INTERVALS = 2;

/** Fixed slack for timer jitter (backgrounded apps clamp timers). */
export const LIVENESS_SLACK_MS = 5_000;

/**
 * Client-side liveness verdict for the realtime socket (web parity:
 * artifacts/web/src/lib/realtime-liveness.ts). The gateway acks every
 * heartbeat, so a healthy socket produces inbound traffic at least once per
 * interval — silence across two full intervals means a half-open connection
 * (venue wifi drop, NAT idle timeout) that must be force-closed so the
 * reconnect path takes over; readyState alone stays OPEN for minutes.
 * A negative silence (clock stepped backwards) never trips it.
 */
export function shouldForceReconnect(
  lastInboundAtMs: number,
  nowMs: number,
  heartbeatIntervalMs: number,
): boolean {
  const silence = nowMs - lastInboundAtMs;
  return (
    silence > heartbeatIntervalMs * LIVENESS_MISSED_INTERVALS + LIVENESS_SLACK_MS
  );
}
